import { DATA_LAB_FEATURES, MAPPING_FEATURES } from '@aytm/app/stats/page/sidebar/components/lib/constants'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// Each switch in render order. `visible` (default: always) gates the switch on resolved
// feature state; `applied` (default: never) lights the "applied" badge from the counts map.
// The DOM id is always `stat-sidebar-switch-${panel}`; the glyph is named explicitly because
// it does not always match the panel (e.g. `mapping` → `insights-portal`). No hover `title`:
// the visible `label` already names the switch.
const SWITCHES = [
  { panel: 'target', icon: 'target-v2', label: 'Target' },
  { panel: 'reports', icon: 'report-v2', label: 'Reports', applied: counts => 0 < counts.reports },
  {
    panel: 'view-options',
    icon: 'view-options-v2',
    label: 'View',
    visible: ctx => !ctx.isHidden('view_options')
  },
  {
    panel: 'coding',
    icon: 'coding-v2',
    label: 'Coding',
    // Gated on capability alone. A virtual or Xpert report keeps the switch: the panel builds
    // itself inert and prepends the prompt naming the report to switch to, as the other gated
    // panels do, and withholding the switch is what made that prompt unreachable.
    visible: ctx => !(ctx.features['oe_tags_view']?.disabled && ctx.features['oe_sentiments_view']?.disabled)
  },
  {
    panel: 'filters',
    icon: 'filter-v2',
    label: 'Filters',
    visible: ctx => !ctx.isHidden('filters'),
    applied: counts => 0 < counts.filters
  },
  {
    panel: 'data-lab',
    icon: 'data-lab-v2',
    label: 'Data Lab',
    visible: ctx => !DATA_LAB_FEATURES.every(type => ctx.isHidden(type))
  },
  {
    panel: 'weighting',
    icon: 'weighting-v2',
    label: 'Weighting',
    visible: ctx => !ctx.isHidden('stat_weighting'),
    applied: counts => counts.weighting
  },
  {
    panel: 'data-quality',
    icon: 'data-quality',
    label: 'Quality',
    visible: ctx => !ctx.isHidden('data_quality_report')
  },
  { panel: 'color-options', icon: 'colors-v2', label: 'Colors' },
  { panel: 'export', icon: 'export-v2', label: 'Export', visible: ctx => !ctx.isHidden('export') },
  {
    panel: 'mapping',
    icon: 'insights-portal',
    label: 'Mapping',
    visible: ctx => !MAPPING_FEATURES.every(type => ctx.isHidden(type) || ctx.features[`${type}_publish`]?.disabled)
  },
  {
    panel: 'completes',
    icon: 'partial-completes',
    label: 'Completes',
    visible: ctx => PageGlobals.IS_EXTERNAL_SURVEY && !ctx.isHidden('partial_completes')
  }
]

// The one reading of feature state the switches are gated on. A switch is withheld when its
// feature is hidden, and also when a guest meets an Xpert report, where a disabled feature is
// withheld rather than shown inert.
export const buildSwitchContext = ({ features = {}, isReportVirtual = false, isReportXpert = false } = {}) => {
  const isHidden = type => features[type]?.hidden || (!PageGlobals.USER_ID && isReportXpert && features[type]?.disabled)

  return { features, isReportVirtual, isReportXpert, isHidden }
}

// The switches the strip will draw, in render order. Shared so the placeholder shown while the
// report loads offers the same set the reader is about to get, rather than a guess at it.
export const visibleSwitches = ctx => SWITCHES.filter(item => !item.visible || item.visible(ctx))
